// src/components/Navigation.jsx
import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { ShoppingCart } from "lucide-react";
import { useCartContext } from "../components/CartContext";
import { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { accessToken, user, role: authRole, logout: logoutAuth } = useAuth();

  const token = accessToken;
  const role = user?.role ?? authRole;

  const { totalItems } = useCartContext();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const { clearCart } = useCartContext();


  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  const handleLogout = () => {
    clearCart(); // Clear cart on logout
    logoutAuth();
    console.info("User logged out");
    if (window.chatSocket) {
      window.chatSocket.close(); // close WS globally
      console.info("WebSocket connection closed on logout");
    }
    navigate("/authlogin");
  };

  const linkClasses = ({ isActive }) =>
    isActive
      ? "text-blue-700 font-semibold"
      : "text-gray-700 hover:text-blue-600";

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4">
      <div className="
        max-w-7xl mx-auto flex items-center justify-between 
        p-4 bg-white/40 backdrop-blur-md 
        border border-white/25 rounded-full shadow-md
      ">

        {/* Logo */}
        <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate("/")}>
              <img
                src={"/5buddies-logo.png"}
                alt="5Buddies Logo"
                className="h-10 w-auto rounded-lg"
              />
              <div className="text-xl font-semibold text-gray-800">
            5BUDDIES
          </div>
        </div>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-6">

          <NavLink to="/" className={linkClasses}>Home</NavLink>
          <NavLink to="/shop" className={linkClasses}>Shop</NavLink>
          {/* <NavLink to="/home" className={linkClasses} onClick={() => setIsOpen(false)}>Creatikalp</NavLink> */}
          {token && role === "ADMIN" && (
            <NavLink to="/admin/products" className={linkClasses}>Products</NavLink>
          )}
          {/* <NavLink to="/cart" className={linkClasses}>Cart</NavLink> */}

          {token && role === "ADMIN" && (
            <NavLink to="/admin/dashboard" className={linkClasses}>
              Dashboard
            </NavLink>
          )}

          {!token ? (
            <>
              <NavLink to="/authlogin" className={linkClasses}>Login</NavLink>
            </>
          ) : (
            <button
              onClick={handleLogout}
              className="text-red-500 font-medium hover:text-red-700"
            >
              Logout
            </button>
          )}
          {/* Cart Icon */}
          <NavLink to="/cart" className="relative">
            <ShoppingCart className="w-6 h-6 text-gray-700" />
            {totalItems > 0 && (
              <span className="
                        absolute -top-2 -right-2 bg-red-500 text-white 
                        text-xs w-5 h-5 flex items-center justify-center 
                        rounded-full shadow
                      ">
                {totalItems}
              </span>
            )}
          </NavLink>

          <div className="relative" ref={profileRef}>
            {/* Profile Bubble */}
            <button
              onClick={() => setProfileOpen(prev => !prev)}
              className="w-9 h-9 rounded-full bg-white/40 backdrop-blur-md
               flex items-center justify-center text-gray-700
               hover:bg-white/60 transition shadow-sm"
            >
              <span className="text-sm font-semibold">
                {user?.userId || user?.id ? "U" : "?"}
              </span>
            </button>

            {/* Dropdown */}
            {profileOpen && (
              <div
                className="
        absolute right-0 mt-3 w-48
        rounded-2xl
        bg-white/60 backdrop-blur-lg
        shadow-xl
        border border-white/40
        overflow-hidden
      "
              >
                <NavLink
                  to="/orders"
                  onClick={() => setProfileOpen(false)}
                  className="
          block px-4 py-3 text-sm text-gray-700
          hover:bg-white/70 transition
        "
                >
                  Order History
                </NavLink>

                {/* <button
                  onClick={() => {
                    setProfileOpen(false);
                    handleLogout(); // 👈 existing logout function
                  }}
                  className="
          w-full text-left px-4 py-3 text-sm text-red-500
          hover:bg-white/70 transition
        "
                >
                  Logout
                </button> */}
              </div>
            )}
          </div>

        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 rounded bg-white/30"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="
          md:hidden mt-3 mx-6 bg-white/70 backdrop-blur-md 
          border border-white/30 rounded-2xl shadow-md p-4
        ">
          <div className="flex flex-col space-y-4 text-gray-700">

            <NavLink to="/" className={linkClasses} onClick={() => setIsOpen(false)}>Home</NavLink>
            <NavLink to="/shop" className={linkClasses} onClick={() => setIsOpen(false)}>Shop</NavLink>
            <NavLink to="/cart" className={linkClasses} onClick={() => setIsOpen(false)}>Cart</NavLink>

            {token && role === "ADMIN" && (
              <NavLink
                to="/admin/dashboard"
                className={linkClasses}
                onClick={() => setIsOpen(false)}
              >
                Dashboard
              </NavLink>
            )}

            {!token ? (
              <>
                <NavLink
                  to="/authlogin"
                  className={linkClasses}
                  onClick={() => setIsOpen(false)}
                >
                  Login
                </NavLink>

                <NavLink
                  to="/register"
                  className={linkClasses}
                  onClick={() => setIsOpen(false)}
                >
                  Register
                </NavLink>
                <NavLink to="/home" className={linkClasses} onClick={() => setIsOpen(false)}>Creatikalp</NavLink>
              </>
            ) : (
              <button
                onClick={() => {
                  handleLogout();
                  setIsOpen(false);
                }}
                className="text-red-500 hover:text-red-700 font-medium text-left"
              >
                Logout
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
